import React, { useMemo } from "react";
import { Box, Grid, IconButton, Tooltip,Typography } from "@mui/material";

// Icon packs (tree‑shaken automatically by react‑icons)
import * as FaIcons from "react-icons/fa";
import * as MdIcons from "react-icons/md";
import * as RiIcons from "react-icons/ri";
import * as LuIcons from "react-icons/lu";

/**
 * -------------------------------------------------------------------
 * 300 CRM‑FOCUSED ICONS (75 per library)
 * -------------------------------------------------------------------
 * A broad, curated set of icons useful for CRM modules: contacts, leads,
 * deals, tasks, analytics, finance, e‑commerce, etc. Any icon names that
 * don’t exist in your installed icon sets are skipped at runtime.
 */
const FA_NAMES = [
    "FaUser", "FaUsers", "FaUserTie", "FaAddressBook", "FaAddressCard", "FaIdBadge", "FaIdCard", "FaPhone", "FaPhoneAlt", "FaMobileAlt", "FaEnvelope", "FaMailBulk", "FaComment", "FaComments", "FaCommentDots", "FaCommentAlt", "FaBell", "FaCalendar", "FaCalendarAlt", "FaCalendarCheck", "FaCalendarDay", "FaCalendarWeek", "FaTasks", "FaClipboard", "FaClipboardCheck", "FaClipboardList", "FaBriefcase", "FaHandshake", "FaMoneyBill", "FaMoneyCheck", "FaMoneyCheckAlt", "FaFileInvoice", "FaFileInvoiceDollar", "FaFileContract", "FaFileSignature", "FaFileAlt", "FaFile", "FaFolder", "FaFolderOpen", "FaTags", "FaTag", "FaChartBar", "FaChartLine", "FaChartPie", "FaSearch", "FaFilter", "FaSort", "FaChevronUp", "FaChevronDown", "FaChevronLeft", "FaChevronRight", "FaPlus", "FaMinus", "FaTimes", "FaCheck", "FaEdit", "FaSave", "FaUpload", "FaDownload", "FaCloudUploadAlt", "FaCloudDownloadAlt", "FaSync", "FaCog", "FaCogs", "FaTools", "FaLock", "FaUnlock", "FaKey", "FaSitemap", "FaProjectDiagram", "FaPaperclip", "FaThumbsUp", "FaShareAlt", "FaPrint", "FaGlobe"
];

const MD_NAMES = [
    "MdPerson", "MdPeople", "MdGroupAdd", "MdPersonAdd", "MdContacts", "MdContactPhone", "MdContactMail", "MdAccountBox", "MdBadge", "MdBusinessCenter", "MdWork", "MdWorkOutline", "MdPhone", "MdPhoneAndroid", "MdPhoneInTalk", "MdEmail", "MdAlternateEmail", "MdChat", "MdComment", "MdForum", "MdNotifications", "MdNotificationsActive", "MdEvent", "MdEventAvailable", "MdEventNote", "MdAssignment", "MdAssignmentTurnedIn", "MdChecklist", "MdTask", "MdCheckCircle", "MdCheck", "MdClose", "MdAdd", "MdRemove", "MdEdit", "MdSave", "MdCloudUpload", "MdCloudDownload", "MdFileCopy", "MdFolder", "MdFolderOpen", "MdLabel", "MdLabelImportant", "MdBarChart", "MdInsertChart", "MdPieChart", "MdShowChart", "MdTimeline", "MdTrendingUp", "MdTrendingDown", "MdSearch", "MdFilterList", "MdSort", "MdArrowUpward", "MdArrowDownward", "MdArrowBack", "MdArrowForward", "MdSettings", "MdSettingsApplications", "MdTune", "MdLock", "MdLockOpen", "MdKey", "MdVpnKey", "MdSync", "MdRefresh", "MdPlayArrow", "MdPause", "MdStop", "MdAccountTree", "MdSchema", "MdAttachFile", "MdThumbUp", "MdShare", "MdPrint"
];

const RI_NAMES = [
    "RiUserLine", "RiUser3Line", "RiUserAddLine", "RiGroupLine", "RiTeamLine", "RiContactsBookLine", "RiPhoneLine", "RiPhoneFindLine", "RiMailLine", "RiMessage2Line", "RiChatSmile3Line", "RiChat1Line", "RiNotificationLine", "RiCalendarLine", "RiCalendar2Line", "RiCalendarEventLine", "RiCalendarCheckLine", "RiFileLine", "RiFileTextLine", "RiFileListLine", "RiFileChartLine", "RiFilePaper2Line", "RiFileExcel2Line", "RiFolderLine", "RiFolderOpenLine", "RiPriceTagLine", "RiPriceTag3Line", "RiBarChartLine", "RiBarChart2Line", "RiPieChartLine", "RiLineChartLine", "RiSearchLine", "RiFilter3Line", "RiSortDesc", "RiArrowUpLine", "RiArrowDownLine", "RiArrowLeftLine", "RiArrowRightLine", "RiAddLine", "RiSubtractLine", "RiCloseLine", "RiCheckLine", "RiEdit2Line", "RiSaveLine", "RiUpload2Line", "RiDownload2Line", "RiSettings3Line", "RiSettings5Line", "RiLockLine", "RiLock2Line", "RiKey2Line", "RiRepeatLine", "RiRefreshLine", "RiPlayLine", "RiPauseLine", "RiStopLine", "RiGitBranchLine", "RiOrganizationChart", "RiAttachment2", "RiThumbUpLine", "RiShareForwardLine", "RiPrinterLine", "RiGlobalLine", "RiHandCoinLine", "RiMoneyDollarBoxLine", "RiExchangeDollarLine", "RiShoppingBagLine", "RiShoppingCartLine", "RiHandbagLine", "RiVipCrownLine", "RiCoupon2Line", "RiWalletLine", "RiBankLine", "RiBriefcaseLine", "RiHandHeartLine"
];

const LU_NAMES = [
    "LuUser", "LuUsers", "LuUserPlus", "LuUserMinus", "LuAddressBook", "LuPhone", "LuPhoneCall", "LuMail", "LuMessageCircle", "LuMessageSquare", "LuBell", "LuCalendar", "LuCalendarCheck", "LuCalendarPlus", "LuCalendarMinus", "LuFile", "LuFileText", "LuFilePlus", "LuFileMinus", "LuFolder", "LuFolderOpen", "LuTag", "LuTags", "LuChartBar", "LuBarChart4", "LuPieChart", "LuLineChart", "LuTrendingUp", "LuTrendingDown", "LuSearch", "LuFilter", "LuSortAsc", "LuSortDesc", "LuChevronUp", "LuChevronDown", "LuChevronLeft", "LuChevronRight", "LuPlus", "LuMinus", "LuX", "LuCheck", "LuEdit", "LuSave", "LuUploadCloud", "LuDownloadCloud", "LuSettings2", "LuSettings", "LuLock", "LuUnlock", "LuKey", "LuRefreshCcw", "LuPlay", "LuPause", "LuStop", "LuTreeStructure", "LuHierarchy", "LuPaperclip", "LuThumbsUp", "LuShare2", "LuPrinter", "LuGlobe", "LuBriefcase", "LuHandshake", "LuDollarSign", "LuStore", "LuShoppingCart", "LuCreditCard", "LuWallet", "LuCoins", "LuInvoice", "LuActivity", "LuImage", "LuChart", "LuMonitor", "LuDatabase"
];

// Combine into a single registry of { lib, name }
const ICON_CANDIDATES = [
    ...FA_NAMES.map((name) => ({ lib: FaIcons, name })),
    ...MD_NAMES.map((name) => ({ lib: MdIcons, name })),
    ...RI_NAMES.map((name) => ({ lib: RiIcons, name })),
    ...LU_NAMES.map((name) => ({ lib: LuIcons, name }))
];

/**
 * Resolve icons that actually exist in the current react‑icons build.
 */
const useResolvedIcons = () =>
    useMemo(
        () =>
            ICON_CANDIDATES.reduce((acc, { lib, name }) => {
                const IconComponent = lib?.[name];
                if (typeof IconComponent === "function") acc.push({ name, Icon: IconComponent });
                else if (process.env.NODE_ENV !== "production")
                    console.warn(`[IconPicker] Skipped missing icon: ${name}`);
                return acc;
            }, []),
        []
    );

/**
 * IconPicker component
 * --------------------
 * Displays a scrollable, bordered grid of ~300 CRM‑centric icons.
 * Selecting one highlights it and calls `onSelect(iconName)`.
 */
export default React.memo(function IconPicker({ onSelect, selected = null,title = "Choose an Icon", maxHeight = 480 }) {
    const icons = useResolvedIcons();

    return (
        <Box
            sx={{
                width: "100%",
                p: 2,
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
                backgroundColor: "background.paper",
                maxHeight,
                overflowY: "auto"
            }}
        >
            <Typography variant="h6" component="h1" sx={{ mb: 2 }}>
                {title}
            </Typography>

            <Grid container spacing={1} columns={{ xs: 12, sm: 12, md: 12 }}>
                {icons.map(({ name, Icon }) => (
                    <Grid item xs={3} sm={2} md={1} key={name}>
                        <Tooltip title={name} arrow>
                            <IconButton
                                onClick={() => onSelect?.(name)}
                                sx={{
                                    borderRadius: 2,
                                    border: 2,
                                    borderColor: selected === name ? "primary.main" : "transparent",
                                    transition: "border-color 0.15s ease-in-out",
                                    "&:hover": { borderColor: "primary.light" }
                                }}
                            >
                                <Icon size={24} />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
})

